import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { List } from "../../../../../../themes/Default/components/List";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import Toaster from "../../../../../../themes/Default/components/Toaster/Toaster";
import { studentAddressManagement } from "../../../../../../hooks/api/StudentAddressManagement/StudentAddressManagement";

const Address = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 100;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedAddress, setSelectedAddress] = useState([]);
    const [address, setAddress] = useState();
    const { useStudentAddressListMutation, useDeleteStudentAddressMutation } = studentAddressManagement;
    const [addressList] = useStudentAddressListMutation();
    const [deleteAddress] = useDeleteStudentAddressMutation();

    const tabs = [
        { title: t("schools.title"), onClick: () => gotoSchoolTab()},
        { title: t("certificates.title"), onClick: () => gotoCertificateTab() },
        { title: t("address.title"), isActive: true },
    ];

    const gotoSchoolTab = () => {
        navigate("/settings/students/schools");
    };

    const gotoCertificateTab = () => {
        navigate("/settings/students/certificates");
    };

    const gotoNew = () => {
        navigate("/settings/students/address/add");
    };

    const gotoEdit = (addressId) => {
        navigate("/settings/students/address/edit/" + addressId);
    };

    const onPageChange = (currentPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedAddress((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedAddress?.length; i++) {
                            const addressId = selectedAddress[i];
                            deleteAddress(addressId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    addressList(filters).then((result) => {
                                        const address = result?.data?.records;
                                        setAddress(address);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(addressId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        if (selectedAddress.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedAddress]);

    useEffect(() => {
        addressList(filters).then((result) => {
            const address = result?.data?.records;
            setAddress(address);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [filters, addressList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("address.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("address.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("students.title")} />
                    <Link title={t("address.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                    <List>
                        <ListHeader>
                        </ListHeader>
                        <ListBody>
                            <TableHead>
                                <TableRow>
                                    <TableHeadCell></TableHeadCell>
                                    <TableHeadCell></TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("address.name")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell></TableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {address?.length > 0 &&
                                    address?.map((address, index) => {
                                        return (
                                            <TableRow key={address.id}>
                                                <TableDataCell>
                                                    <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(address.id)}></Button>
                                                </TableDataCell>
                                                <TableDataCell>
                                                    <Checkbox name="isBlockClass" value={address.id} onChange={() => onSelectionChange(address.id)} />
                                                </TableDataCell>
                                                <TableDataCell>{address.name}</TableDataCell>
                                                <TableDataCell></TableDataCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </ListBody>
                    </List>
                    <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
            </Main>
        </DefaultLayout>
    );
};

export default Address;
